import Config from '@config';
import { ReferenceBook } from '@gasbi/shared';

import { injectSupplySlice } from '@/app/supplyApi';
import { FiltersGrid, Pagination } from '@/features/types';
import {
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@/features/utils';

export interface ConsumerCategoryReferenceBook extends ReferenceBook {
  parentId?: string | null;
  /** The ПСО mark Admin sets on the category — what a ПСО lot's distributions may name. */
  hasPso?: boolean;
  symbolEic?: string | null;
}

/** «ПОН (постачальник останньої надії)» — the one category a ПОН lot serves (seeded, same id everywhere). */
export const LAST_RESORT_SUPPLIER_CATEGORY_ID =
  '019e2c0a-5317-71be-a2d0-142cab254964';

/**
 * GSB-7141 §8-10: «Промисловість» and «Виробники е/е» — mirrors the backend's
 * ConsumerCategoryIds.Commercial. Only these two choose a price type and a supply tariff type;
 * every other category is regulated and takes the fixed ПСО price for its category.
 */
export const COMMERCIAL_CONSUMER_CATEGORY_IDS = [
  '01a0d0b6-4000-7b3a-8f21-5c9e7d4a6102',
  '019f0a5c-9d28-757e-8665-57fc96a11659',
];

export const isCommercialConsumerCategory = (
  categoryId?: string | number | null,
) => COMMERCIAL_CONSUMER_CATEGORY_IDS.includes(String(categoryId));

const api = injectSupplySlice({
  baseUrl: `${Config.VITE_API_URL}/consumer-categories`,
  tagTypes: ['ConsumerCategory'],
  endpoints: builder => ({
    getConsumerCategories: builder.query<ConsumerCategoryReferenceBook[], void>(
      {
        query: () => ({ url: '', method: 'GET' }),
        providesTags: () => ['ConsumerCategory'],
      },
    ),
    getConsumerCategoriesI: builder.infiniteQuery<
      ConsumerCategoryReferenceBook[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        // TODO: make sure that this endpoint use Gridify and change to /query
        url: '/',
        method: 'GET',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['ConsumerCategory'],
    }),
  }),
});

export const consumerCategoriesApi = api;

export const { useGetConsumerCategoriesQuery } = api;

export const useGetConsumerCategoriesInfiniteQuery = normalizeInfiniteQueryData(
  api.useGetConsumerCategoriesIInfiniteQuery,
);
